import type { MouseEvent } from "react";
import MuiLink from "@mui/material/Link";
import { tokens } from "../tokens";

/**
 * Shared skip link (AC-1, ADR-20261013-1z2y/ADR-20261013-zdg2, WCAG 2.2
 * SC 2.4.1 bypass blocks). Rendered by `PortalHeader` as its first child —
 * not exported from the design-system barrel, matching `PortalFooter` and
 * `PortalHeader` themselves, since `PortalLayout` is the only sanctioned
 * way a screen reaches any of the three.
 *
 * Placed INSIDE `<header>` rather than as a sibling before it: the shared
 * structural oracle (`test/support/portal-structure.ts`) asserts
 * `header.previousElementSibling` is null for every screen using
 * `PortalLayout` — a real, already-relied-upon contract this component
 * must not break. Nesting inside `header`, before the logo, satisfies
 * both constraints at once: nothing precedes `<header>` itself, and this
 * is still the first FOCUSABLE element in the document (the logo is an
 * `<img>`, the "Renewal portal" label a plain `<span>` — neither is
 * reachable by Tab), so it is still the very first stop a keyboard user
 * reaches, exactly as AC-1 requires.
 *
 * Off-screen until focused (a clip-based visually-hidden pattern, not
 * `display: none`/`visibility: hidden`, either of which would also remove
 * it from the tab order) and rendered above everything else, pinned to the
 * top-left of the viewport, once it is. The 3px focus ring and the 44×44
 * CSS pixel minimum target both come from the shared tokens, the same
 * values every other interactive control in this design system uses.
 *
 * Activating it moves focus to `#main-content` directly (`event
 * .preventDefault()` plus a manual `.focus()` call) rather than relying on
 * the browser's own fragment-navigation focus behaviour, which is
 * inconsistent across browsers and does not fire at all under jsdom/RTL —
 * this way the behaviour is the same, and deterministically testable,
 * everywhere. `href="#main-content"` is kept for progressive enhancement
 * (a screen reader or no-JS fallback can still follow it as an ordinary
 * same-page link); `PortalLayout`'s own `main` carries `tabIndex={-1}` so
 * it accepts programmatic focus despite not being natively focusable.
 */
function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
  event.preventDefault();
  const main = document.getElementById("main-content");
  main?.focus();
}

export function SkipLink() {
  return (
    <MuiLink
      href="#main-content"
      onClick={handleClick}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        transform: "translateY(-150%)",
        zIndex: 2000,
        minHeight: 44,
        minWidth: 44,
        display: "inline-flex",
        alignItems: "center",
        // ADR-20261102-h2fd (AC-14): H2's `.skip-link` -- 12px by 16px padding, regular weight,
        // no fill of its own.
        padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
        color: tokens.colour.brand[700],
        fontWeight: tokens.typography.weights.regular,
        fontSize: tokens.typography.variants.body.size,
        textDecoration: "underline",
        "&:focus": {
          transform: "translateY(0)",
          outline: `${tokens.interactionState.focusRing.width}px solid ${tokens.interactionState.focusRing.colourOnLight}`,
          outlineOffset: tokens.interactionState.focusRing.offset,
        },
      }}
    >
      Skip to main content
    </MuiLink>
  );
}
